import { useState } from "react";
import { 
  X, Truck, RefreshCw, Receipt, ChevronRight, 
  Calendar, Users, BarChart2, Clock, CheckCircle, ArrowLeft
} from "lucide-react";
import FacturesPage from "./FacturesPage";
import { CalendrierButton } from './components/calendrier/CalendrierButton';

export default function ChantiersDashboard({ onBack, onOpenPole, chantiersPole }) {
  const [showFactures, setShowFactures] = useState(false);

  // Si on affiche Factures Chantiers
  if (showFactures) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => setShowFactures(false)}
            className="mb-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour aux Chantiers
          </button>
          <FacturesPage onBack={() => setShowFactures(false)} embedded={true} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-950/80 border-b border-slate-800 px-6 py-4 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">🚚 Chantiers</h1>
                <p className="text-sm text-slate-400">Suivi des travaux</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CalendrierButton />
            <button
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* Cards principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          
          {/* Accès au Pôle Chantiers */}
          <button
            onClick={() => onOpenPole && chantiersPole && onOpenPole(chantiersPole)}
            data-testid="pole-chantiers-btn"
            className="bg-gradient-to-br from-orange-900/50 to-amber-900/50 border border-orange-500/30 rounded-xl p-6 hover:border-orange-400 hover:shadow-orange-500/10 hover:shadow-lg transition-all text-left group"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">📋 Dossiers Chantiers</h3>
                <p className="text-orange-300 text-sm">{chantiersPole?.columns_count || '--'} champs</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">
              Accédez à la liste complète des dossiers en phase chantier avec toutes les informations de suivi.
            </p>
            <div className="mt-4 flex items-center gap-2 text-orange-400 text-sm">
              <span>Voir les dossiers</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </button>

          {/* Factures Chantiers */}
          <button
            onClick={() => setShowFactures(true)}
            data-testid="factures-chantiers-btn"
            className="bg-gradient-to-br from-rose-900/50 to-pink-900/50 border border-rose-500/30 rounded-xl p-6 hover:border-rose-400 hover:shadow-rose-500/10 hover:shadow-lg transition-all text-left group"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Receipt className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">🧾 Factures Chantiers</h3>
                <p className="text-rose-300 text-sm">Suivi facturation</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm">
              Gérez les factures liées aux chantiers : création, suivi des paiements et relances.
            </p>
            <div className="mt-4 flex items-center gap-2 text-rose-400 text-sm">
              <span>Gérer les factures</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </button>

          {/* Planning Chantiers - Placeholder */}
          <div className="bg-gradient-to-br from-slate-800/50 to-gray-900/50 border border-slate-600/30 rounded-xl p-6 text-left opacity-60">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-slate-500 to-gray-600 rounded-xl flex items-center justify-center">
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">📅 Planning</h3>
                <p className="text-slate-400 text-sm">Bientôt disponible</p>
              </div>
            </div>
            <p className="text-slate-500 text-sm">
              Visualisez et gérez le planning des interventions et des équipes sur les chantiers.
            </p>
            <div className="mt-4 flex items-center gap-2 text-slate-500 text-sm">
              <span>En développement</span>
              <Clock className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Section Stats rapides */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-slate-400" />
            Aperçu rapide
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-orange-400">--</p>
              <p className="text-sm text-slate-400">Chantiers en cours</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-amber-400">--</p>
              <p className="text-sm text-slate-400">À planifier</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-400">--</p>
              <p className="text-sm text-slate-400">Terminés ce mois</p>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-rose-400">--</p>
              <p className="text-sm text-slate-400">Factures en attente</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4 text-center">
            Les statistiques seront alimentées prochainement
          </p>
        </div>
      </main>
    </div>
  );
}
